import { useEffect, useState } from "react";
import { useError } from "./errorContext";
import {
  registerGlobalErrorSetter,
  unregisterGlobalErrorSetter,
} from "./globalError";
import { CREATE_SALE_REPORT_ERROR, GLOBAL_WARNING } from "./error";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

type AlertContent = {
  message: string;
  severity: "error" | "warning";
  title: string;
};

export default function GlobalAlert() {
  const { error, setError } = useError();

  // Snackbar/Alert stay mounted and animate out after `open` flips false, so
  // this only derives new content while there IS an error — otherwise the
  // close transition would briefly re-render a fallback in place of the
  // message that's fading out.
  const [content, setContent] = useState<AlertContent | null>(null);

  useEffect(() => {
    registerGlobalErrorSetter(setError);
    return () => unregisterGlobalErrorSetter();
  }, [setError]);

  useEffect(() => {
    if (!error) return;

    const mapped = CREATE_SALE_REPORT_ERROR[error] ?? GLOBAL_WARNING[error];
    const isWarning = Boolean(mapped);

    setContent({
      message: mapped || error,
      severity: isWarning ? "warning" : "error",
      title: isWarning ? "Warning" : "Error",
    });
  }, [error]);

  return (
    <Snackbar
      open={!!error}
      // A warning is a full sentence about something that already happened and
      // cannot be retried from the alert — 2s is not long enough to read it.
      autoHideDuration={content?.severity === "warning" ? 6000 : 2000}
      onClose={() => setError(null)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        severity={content?.severity ?? "error"}
        onClose={() => setError(null)}
        variant="standard"
        sx={{ fontFamily: "'Prompt', 'sans-serif'" }}
      >
        <AlertTitle>{content?.title}</AlertTitle>
        <Typography sx={{ fontFamily: "'Prompt', 'sans-serif'" }}>
          {content?.message}
        </Typography>
      </Alert>
    </Snackbar>
  );
}
